import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { noContent, siteResponse, sitesResponse, SLUG_SHAPE, type Site } from "@sitebrew/contracts/v1";
import { keys } from "./query-keys";
import type { DataTablePage, DataTableQuery } from "@/components/data-table";

/**
 * The site registry, as the console sees it (`sitebrew-api` design note 0001).
 *
 * The addressing is the API's and deliberately not a tidier one: a list and a
 * create carry the workspace in the path, a read and an archive carry only the
 * site id. That is §2 — the workspace is a path segment on every route where
 * the caller chooses it — and copying it here keeps the console's URLs and the
 * API's authorisation shape the same shape.
 */

/**
 * `Site` is `@sitebrew/contracts/v1`'s, and so is the shape of a slug: both
 * used to be copies of the API's, held in step by a comment (#289, rows 30 and
 * 31). The slug check here stays deliberately the weaker half — the shape and
 * nothing else. Whether a name is reserved, already taken, or retired by a
 * deletion is the API's to answer, and every one of those answers is shown to
 * the person verbatim.
 */
export function looksLikeSlug(slug: string): boolean {
  return SLUG_SHAPE.test(slug);
}

/**
 * A slug suggested from the name somebody typed.
 *
 * A suggestion and not a rule: it fills the field while it is untouched, and
 * the moment it is edited the person owns it. Diacritics are folded rather than
 * dropped, so "Kavárna Můj Dům" suggests `kavarna-muj-dum` and not `kav-rna`.
 */
export function slugFrom(name: string): string {
  return name
    .normalize("NFKD")
    // The combining marks NFKD just split off, written as escapes: the literal
    // characters are invisible in an editor and survive a copy-paste as noise.
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63)
    .replace(/-+$/g, "");
}

/*
 * `useSites` used to be here — one query for the whole list, read by a
 * `QueryBoundary` around the screen. `sitesPage` replaced it when the list
 * became a table, and the hook is gone rather than left for later: two ways to
 * fetch the same list under the same key is how one of them quietly stops
 * being invalidated.
 */

/**
 * One page of a workspace's sites, for `DataTable`.
 *
 * The paging, the search and the sorting happen **here, in the browser**, and
 * that is not the end state: `GET /v1/workspaces/{ws}/sites` answers with the
 * whole list and takes no `?page=`, `?q=` or `?sort=` — the `/v1` contract
 * (design note 0001 §6) gives it no parameters at all — so there is nothing to
 * pass on yet. A workspace holds a handful of sites
 * in M2, so one request and a slice is the honest shape — and it is written as
 * the one function the table asks, so the day the API grows those parameters
 * this body becomes a URL and nothing else moves.
 *
 * What it must not become is a table that *looks* paged over ten thousand rows
 * while fetching all ten thousand. `total` below is the count after searching,
 * which is what the pager divides — the moment that number stops fitting in one
 * answer, this is the function that has to change, and it is the only one.
 */
export async function sitesPage(
  workspaceId: string,
  query: DataTableQuery,
  options: { signal?: AbortSignal } = {},
): Promise<DataTablePage<Site>> {
  const all = await api(`/workspaces/${workspaceId}/sites`, sitesResponse, {
    signal: options.signal,
  }).then((body) => body.sites);

  const matched = query.search ? all.filter((site) => siteMatches(site, query.search)) : all;
  const ordered = sortSites(matched, query.sorting);
  const from = query.pageIndex * query.pageSize;

  return { rows: ordered.slice(from, from + query.pageSize), total: matched.length };
}

/**
 * What a search term is compared against: the three strings a person could be
 * looking at. `hostname` is null until the site is registered, and the slug is
 * what stands in for it on screen, so both are searched — otherwise typing the
 * address shown in the row would find nothing.
 *
 * `localeCompare`-free on purpose: this is a substring test, and folding case
 * through `toLowerCase` is what makes "Acme" find "acme".
 */
function siteMatches(site: Site, search: string): boolean {
  const needle = search.toLowerCase();
  return [site.name, site.hostname, site.slug].some(
    (value) => value !== null && value.toLowerCase().includes(needle),
  );
}

/**
 * The sorting the table asked for, applied to the answer.
 *
 * The API's own order is **oldest first** — `listSites` orders by `createdAt`
 * ascending (`sitebrew-api`, `packages/core/src/sites.ts`), read there rather
 * than assumed here — and that is what an unsorted table shows, the same order
 * the bordered list showed before it. So this returns the list untouched when
 * nothing is sorted rather than imposing an order of its own: if the newest
 * site belongs at the top, that is a change to the API's `orderBy`, not a
 * second opinion in the browser that the next caller would not share.
 */
function sortSites(sites: Site[], sorting: DataTableQuery["sorting"]): Site[] {
  if (sorting.length === 0) return sites;

  return [...sites].sort((a, b) => {
    for (const { id, desc } of sorting) {
      const order = compareSites(a, b, id);
      if (order !== 0) return desc ? -order : order;
    }
    return 0;
  });
}

function compareSites(a: Site, b: Site, column: string): number {
  if (column === "createdAt") return a.createdAt - b.createdAt;

  // A missing hostname sorts with the slug that stands in for it on screen, so
  // the order matches what somebody is reading rather than putting every
  // unregistered site in one silent clump at the end.
  const value = (site: Site) =>
    column === "hostname" ? (site.hostname ?? site.slug) : String(site[column as keyof Site] ?? "");

  return value(a).localeCompare(value(b));
}

/** One site. `404` covers "not yours" and "archived" alike, as the API does. */
export function useSite(siteId: string) {
  return useQuery({
    queryKey: keys.site(siteId),
    queryFn: () => api(`/sites/${siteId}`, siteResponse).then((body) => body.site),
  });
}

/**
 * Create a site.
 *
 * The refusals are not translated here. The API's `409` says whether a slug is
 * taken by somebody else or retired by this workspace's own deletion, and those
 * are different facts a person acts on differently — re-wording them in the
 * console would be a second copy of a decision that lives in the API (§6).
 */
export function useCreateSite(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string; slug: string }) =>
      api(`/workspaces/${workspaceId}/sites`, siteResponse, {
        method: "POST",
        body: input,
      }).then((body) => body.site),
    onSuccess: (site) => {
      // The new site is readable at its own key before its screen asks, and the
      // list it belongs to is stale — in that order, so the detail screen it
      // navigates to renders from the answer we already have.
      queryClient.setQueryData(keys.site(site.id), site);
      void queryClient.invalidateQueries({ queryKey: keys.sites(workspaceId) });
    },
  });
}

/**
 * Rename a site, which is the whole of editing one (design note §11).
 *
 * Only the name. The address is not editable and the screen does not offer it —
 * a slug becomes a hostname, and a hostname that has been public is never
 * reissued, so "change the address" is a redirect feature rather than a field.
 * The API refuses a body carrying `slug` outright, so a console that offered
 * one would be showing a control whose every use is an error.
 *
 * The answer carries the whole site back, so the detail screen is written from
 * it rather than from what was typed, and the list it belongs to is stale.
 */
export function useRenameSite(site: Pick<Site, "id" | "workspaceId">) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      api(`/sites/${site.id}`, siteResponse, { method: "PATCH", body: { name } }).then(
        (body) => body.site,
      ),
    onSuccess: (renamed) => {
      queryClient.setQueryData(keys.site(renamed.id), renamed);
      void queryClient.invalidateQueries({ queryKey: keys.sites(site.workspaceId) });
    },
  });
}

/**
 * Delete a site, which archives it (decided 2026-08-16, design note §10).
 *
 * `204` and nothing to read back, so the cached site is *removed* rather than
 * updated: the next read of that id is the API's `404`, which is the truth —
 * an archived site is gone from every listing, and a row left in the cache
 * saying `ready` would outlive the thing it describes.
 */
export function useArchiveSite(site: Pick<Site, "id" | "workspaceId">) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api(`/sites/${site.id}`, noContent, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: keys.site(site.id) });
      void queryClient.invalidateQueries({ queryKey: keys.sites(site.workspaceId) });
    },
  });
}
